import React, { useEffect, useRef, useState } from 'react'
import { getErrorMessage } from '@/utils/getErrorMessage'
import {
  Steps,
  Typography,
  Button,
  Breadcrumb,
  Layout,
  Divider,
  Flex,
  Spin,
  message,
} from 'antd'
import { useNavigate, useLocation, useParams, Outlet } from 'react-router-dom'
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons'
import {
  type EventMapping,
  type PixelPlatform,
  type CredentialPayload,
  defaultEventMappings,
} from '@/data'
import { getPixel } from '@/api/lop/pixels/getPixel'
import { createPixel } from '@/api/lop/pixels/createPixel'
import { updatePixel } from '@/api/lop/pixels/updatePixel'
import {
  createPixelEvent,
  updatePixelEvent,
  deletePixelEvent,
} from '@/api/lop/pixels/events'

const { Title } = Typography
const { Content } = Layout

const steps = [
  { title: 'Settings', path: 'settings' },
  { title: 'Events', path: 'events' },
]

export interface PixelEditorContext {
  mode: 'edit' | 'create'
  pixelId: string | null
  name: string
  setName: (name: string) => void
  platform: PixelPlatform
  setPlatform: (platform: PixelPlatform) => void
  pixel_id: string
  setPixelId: (id: string) => void
  api_token: string
  setApiToken: (token: string) => void
  partner_id: number | null
  setPartnerId: (id: number | null) => void
  event_mappings: EventMapping[]
  setEventMappings: (mappings: EventMapping[]) => void
  basePath: string
}

const PixelEditorContent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()

  const isEditMode = Boolean(id)
  const basePath = isEditMode ? `/pixels/${id}/edit` : '/pixels/new'

  const [loading, setLoading] = useState(isEditMode)
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')
  const [platform, setPlatform] = useState<PixelPlatform>('facebook')
  const [pixelIdVal, setPixelIdVal] = useState('')
  const [apiToken, setApiToken] = useState('')
  const [partnerId, setPartnerId] = useState<number | null>(null)
  const [eventMappings, setEventMappings] = useState<EventMapping[]>(
    structuredClone(defaultEventMappings),
  )

  const hasFetched = useRef(false)
  const originalEvents = useRef<EventMapping[]>([])

  useEffect(() => {
    if (!id || hasFetched.current) return
    hasFetched.current = true
    ;(async () => {
      try {
        setLoading(true)
        const pixel = await getPixel(id)
        if (!pixel) return
        setName(pixel.name)
        if (pixel.platform) setPlatform(pixel.platform)
        setPixelIdVal(pixel.pixel_id)
        if (pixel.api_token) setApiToken(pixel.api_token)
        setPartnerId(pixel.partner_id)
        originalEvents.current = structuredClone(pixel.event_mappings)
        setEventMappings(structuredClone(pixel.event_mappings))
      } catch (err) {
        message.error('Failed to load pixel')
        console.error(err)
      } finally {
        setLoading(false)
      }
    })()
  }, [id])

  const buildPayload = () => ({
    name,
    platform,
    pixel_id: pixelIdVal,
    partner_id: partnerId ?? 1,
    credential: {
      credential_type: platform,
      api_token: pixelIdVal,
      api_key: apiToken,
    } as CredentialPayload,
    pixel_events: eventMappings.map((m) => ({
      ...m,
      send_method: m.send_method || 'server',
      event_provider: m.event_provider || platform,
    })),
  })

  const syncEvents = async (pixelId: string) => {
    const current = eventMappings.map((m) => ({
      ...m,
      send_method: m.send_method || 'server',
      event_provider: m.event_provider || platform,
    }))
    const currentIds = new Set(current.filter((m) => m.id).map((m) => m.id))

    // Delete removed events
    for (const event of originalEvents.current) {
      if (event.id && !currentIds.has(event.id)) {
        await deletePixelEvent(pixelId, event.id)
      }
    }

    // Update existing events
    for (const event of current) {
      if (event.id) {
        await updatePixelEvent(pixelId, event.id, event)
      }
    }

    // Create new events
    for (const event of current) {
      if (!event.id) {
        await createPixelEvent(pixelId, event)
      }
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      if (isEditMode && id) {
        const payload = buildPayload()
        if (!apiToken) {
          delete payload.credential.api_key
          delete payload.credential.api_token
        }
        await updatePixel(id, payload)
        await syncEvents(id)
        message.success('Pixel updated')
      } else {
        const created = await createPixel(buildPayload())
        message.success('Pixel created')
        navigate(`/pixels/${created.id}/edit/settings`)
      }
    } catch (err) {
      const fallback = isEditMode
        ? 'Failed to update pixel'
        : 'Failed to create pixel'
      message.error(getErrorMessage(err, fallback))
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const currentStep = steps.findIndex((step) =>
    location.pathname.endsWith(`/${step.path}`),
  )
  const activeStep = currentStep === -1 ? 0 : currentStep

  const handleStepChange = (step: number) => {
    navigate(`${basePath}/${steps[step].path}`)
  }

  const goBack = () => {
    if (activeStep > 0) {
      navigate(`${basePath}/${steps[activeStep - 1].path}`)
    }
  }

  const goNext = () => {
    if (activeStep < steps.length - 1) {
      navigate(`${basePath}/${steps[activeStep + 1].path}`)
    }
  }

  const context: PixelEditorContext = {
    mode: isEditMode ? 'edit' : 'create',
    pixelId: id || null,
    name,
    setName,
    platform,
    setPlatform,
    pixel_id: pixelIdVal,
    setPixelId: setPixelIdVal,
    api_token: apiToken,
    setApiToken,
    partner_id: partnerId,
    setPartnerId,
    event_mappings: eventMappings,
    setEventMappings,
    basePath,
  }

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    )
  }

  return (
    <div>
      <Flex vertical gap="middle" style={{ width: '100%' }}>
        <Breadcrumb
          items={[
            { title: 'Pixels', href: '/pixels' },
            { title: isEditMode ? `Edit: ${name}` : 'New Pixel' },
          ]}
        />

        <Flex align="center" gap="middle">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/pixels')}
          />
          <Title level={3} style={{ margin: 0 }}>
            {isEditMode ? `Edit Pixel: ${name}` : 'Create New Pixel'}
          </Title>
        </Flex>

        <Flex gap="large" style={{ marginTop: 16 }}>
          <div
            style={{
              borderRight: '1px solid #f0f0f0',
              paddingRight: 24,
              minWidth: 200,
            }}
          >
            <Steps
              current={activeStep}
              onChange={handleStepChange}
              orientation="vertical"
              items={steps.map((step) => ({
                title: step.title,
              }))}
              style={{ position: 'sticky', top: 24 }}
            />
          </div>
          <Content style={{ flex: 1, minHeight: 400 }}>
            <Outlet context={context} />

            <Divider />

            <Flex justify="space-between" style={{ paddingTop: 16 }}>
              <Button
                onClick={goBack}
                disabled={activeStep === 0}
                icon={<ArrowLeftOutlined />}
              >
                Back
              </Button>

              {activeStep < steps.length - 1 ? (
                <Button type="primary" onClick={goNext}>
                  Next
                  <ArrowRightOutlined />
                </Button>
              ) : (
                <Button type="primary" onClick={handleSave} loading={saving}>
                  {isEditMode ? 'Save Changes' : 'Create Pixel'}
                </Button>
              )}
            </Flex>
          </Content>
        </Flex>
      </Flex>
    </div>
  )
}

export const PixelEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  return <PixelEditorContent key={id ?? 'new'} />
}
